import { pageTypes } from 'config/pageTypes';
import lockerHero from 'assets/images/sgp/SGPHero/self-storage-lockers-hero.webp';
import selfStorageSizeGuide from 'assets/images/sgp/featuredContentImages/mediumSizeRelatedContentImage1.webp';
import cheapestStorageUnitsForRent from 'assets/images/sgp/featuredContentImages/cheapest-storage-units-for-rent.webp';
import storageUnitPrices from 'assets/images/sgp/featuredContentImages/mediumSizeRelatedContentImage2.webp';
import downsizeHomeWithStorageUnits from 'assets/images/sgp/featuredContentImages/mediumSizeRelatedContentImage4.webp';
import storageLocker from 'assets/images/sgp/SizeGuide/storage-locker.svg';
import { linkToOtherPSPsBlock } from './shared';

const heroContent = {
	title: 'Affordable Self Storage Locker Units Near You',
	description:
		"You know that closet where you shove everything and hope it magically tidies itself up? The one that avalanches every time you open it? That's where self storage lockers come in. Whether you're searching for cheap storage lockers to hold seasonal items or need locker storage for your college student's dorm overflow, these compact spaces offer an affordable solution without paying for square footage you don't need.",
	image: lockerHero,
	storageType: 'Locker Self Storage',
	subheading: 'Find Budget Friendly Locker Self Storage',
	CTAtext: 'Find Storage',
	searchPlaceholder: 'Enter ZIP Code or City Name',
};

const videoContent = {
	title: 'What Are Self Storage Lockers?',
	background: 'blueGradient',
	pageType: pageTypes.SGP,
	noPaddingBottom: true,
	modules: [
		{
			type: 'video',
			props: {
				pageType: pageTypes.SGP,
				content: {
					title: 'What Are Self Storage Lockers?',
					description:
						"Storage lockers are small, secure compartments—usually ranging from about 3×3 feet up to 5×5 feet. They work like oversized school lockers, but for your personal belongings. Some facilities group them in with mini storage units, but they have some differences from their fellow compact storage spaces.\n\nYou'll usually find storage lockers inside [climate-controlled buildings](https://www.selfstorage.com/storage-types/climate-controlled-self-storage), with the same security features as larger units: keypad access, surveillance cameras, and individual locks. The difference? You're paying for exactly the space you need without the costly space premiums.",
					videoSrc:
						'https://www.youtube.com/embed/ZXoE482g6Z4?rel=0&playsinline=1&enablejsapi=1',
				},
			},
		},
	],
};
const pricingContent = [
	{
		type: 'sizeComparisonSection',
		noPadding: true,
		tabAlign: 'left',
		props: {
			content: {
				title: 'When Storage Lockers Make Perfect Sense',
				description:
					'Is a storage locker the right size storage unit for you? Our quick guide explains when a storage locker can be a valuable asset.',
				showImage: false,
			},
			storageData: [
				{
					size: 'College Storage',
					heading: 'College Storage',
					desc: [
						'- Students can store their belongings during semester breaks or between housing situations\n- Lockers near campus are a good place to store textbooks, mini-fridges, and seasonal clothing.',
					],
				},
				{
					size: 'Apartment Overflow',
					heading: 'Apartment Overflow',
					desc: [
						"- If your home, studio, or [apartment doesn't offer enough on-site storage](https://www.selfstorage.com/blog/small-apartment-storage-hacks/), lockers provide additional room without costly upgrades.\n- Store seasonal decor, clothing, and keep furniture on rotation with locker self storage.",
					],
				},
				{
					size: 'Seasonal Wardrobes',
					heading: 'Seasonal Wardrobes',
					desc: [
						'- Keep your winter clothes fresh during the summer, and summer clothing out of sight during the colder months.\n- Make room in your closet for things that matter most all year-round.',
					],
				},
				{
					size: 'Decluttering',
					heading: 'Decluttering',
					desc: [
						'- Make cleaning and decluttering more manageable with self storage lockers.\n- Keep sentimental items safe and secure, without overcrowding your mantle.',
					],
				},
				{
					size: 'Family Expansion',
					heading: 'Family Expansion',
					desc: [
						'- If your family is growing, then your space at home is shrinking. Storage lockers help manage growing pains associated with family expansion.\n- Store baby gear, clothes that have gotten too small, and sporting goods in the off season.\n- Hand-me-downs stay safe and ready for your next kid, without overcrowding the closet.',
					],
				},
			],
		},
	},
];

const faqSectionContent = {
	header: 'Common Questions About Storage Lockers',
	description: '',
	image: storageLocker,
	faqs: [
		{
			question: 'What are storage lockers used for?',
			answer: "Storage lockers are small rental spaces (typically 3×3 to 5×5 feet) perfect for seasonal items, college storage, extra clothes, holiday decorations, sports equipment, and small furniture that don't fit at home.",
		},
		{
			question: 'How much do cheap storage lockers cost?',
			answer: 'Affordable locker storage starts around $35.27/month for a 5×5 unit, making them one of the most budget-friendly storage options for people with limited storage needs.',
		},
		{
			question: 'Are storage lockers climate-controlled?',
			answer: 'Most locker self-storage units are located indoors and offer climate control, which is important for protecting clothing, documents, photos, and electronics from temperature and humidity damage.',
		},
		{
			question: "What's the smallest storage locker I can rent?",
			answer: 'The smallest locker storage sizes start around 3×3 feet (9 square feet), and are commonly available and offer better value.',
		},
		{
			question: 'Can I access my storage locker anytime?',
			answer: 'Access hours vary by facility, but many locker self-storage locations offer extended or 24-hour access with secure keypad entry for maximum convenience.',
		},
		{
			question: 'Do I need insurance for a storage locker?',
			answer: "While not required, tenant insurance is recommended and usually costs $8-15/month. Check if your renter's or homeowner's insurance already covers stored items before purchasing facility insurance.",
		},
		{
			question:
				'How do I know if I need a storage locker or a full unit?',
			answer: "If your items would fit in a walk-in closet or small bedroom, cheap storage lockers work perfectly — if you're storing full-room furniture sets or appliances, you'll likely need a 10×10 or larger unit.",
		},
	],
};

const reviewsContent = {
	headerTitle: '',
	reviews: [
		{
			id: 'Locker Storage Review 1',
			title: 'We had a great experience',
			stars: 5,
			text: 'We had a great experience, checking in and getting a locker that was suitable for us. The lady at the counter was very informative and made the whole process go by very smoothly. I highly recommend this location.',
			customerName: 'Nancy Harris',
			date: '2025-10-18',
			link: 'https://www.trustpilot.com/reviews/68f27bd0115331b7335db83e',
		},
		{
			id: 'Locker Storage Review 2',
			title: 'Excellent customer service',
			stars: 5,
			text: 'The sales person was very helpful in setting everything up. The storage locker was very clean and I felt safe because it was gated. Also a great value.',
			customerName: 'Deborah Economou',
			date: '2025-09-29',
			link: 'https://www.trustpilot.com/reviews/68da2dc6df66fb01f124b2e3',
		},
		{
			id: 'Locker Storage Review 3',
			title: 'Top Notch Customer Service',
			stars: 5,
			text: 'The woman at reception was incredibly kind and helpful. I needed to reserve a locker quickly to store some items and she helped me find a locker and register so quickly that I was in and out in 20 mins and made it to my next destination right on time. Some of the best customer service I have ever experienced.',
			customerName: 'Rayon Richards',
			date: '2025-04-14',
			link: 'https://www.trustpilot.com/reviews/67fd2a4c36d52e87aa91bb2f',
		},
		{
			id: 'Locker Storage Review 4',
			title: 'The from light to dark guided by friendly associates',
			stars: 5,
			text: "The friendly and helpful associates guided me through my first experience with renting a storage locker with ease. The online contract and signing was simple even for an old man like me who isn't great with technology.",
			customerName: 'Larry Hurt',
			date: '2024-09-28',
			link: 'https://www.trustpilot.com/reviews/66f821ff25d7a6957689ab50',
		},
	],
};

const relatedContent = {
	heading: 'Related Resources',
	type: 'unitCards',
	id: 'locker-storage-related-content',
	data: {
		slug: 'locker-storage-related-content',
		unitCards: [
			{
				title: 'Storage',
				url: 'https://www.selfstorage.com/blog/self-storage-unit-size-guide-2/',
				layout: false,
				plain: true,
				date: '',
				image: selfStorageSizeGuide,
				content:
					'Self Storage Size Guide: Finding the Perfect Storage Unit Size',
			},
			{
				title: 'Storage',
				url: 'https://www.selfstorage.com/blog/cheapest-storage-unit-prices/',
				layout: false,
				plain: true,
				date: '',
				image: cheapestStorageUnitsForRent,
				content: 'What is the Cheapest Storage Unit You Can Rent?',
			},
			{
				title: 'Storage',
				url: 'https://www.selfstorage.com/blog/storage-unit-prices/',
				layout: false,
				plain: true,
				date: '',
				image: storageUnitPrices,
				content: 'Storage Unit Prices: How Much Is a Storage Unit?',
			},
			{
				title: 'Storage',
				url: 'https://www.selfstorage.com/blog/how-storage-reduces-cost-of-living/',
				layout: false,
				plain: true,
				date: '',
				image: downsizeHomeWithStorageUnits,
				content:
					'The Benefits of Downsizing Your Home: How to Reduce the Cost of Living with Self Storage',
			},
		],
	},
};

const lockerComparisonContent = {
	title: 'Locker Comparison Section',
	modules: [
		{
			type: 'lockerComparison',
			props: {
				content: {
					title: 'Storage Lockers vs. Regular Storage Units',
					description:
						"What's the difference between a self storage locker and a standard self storage unit? Square (and cubic) footage. Take a look at how storage lockers stack up against a common 10x10 unit.",
					footerText:
						"The math is simple: if you don't need 100 square feet, why pay for it? Cheap storage lockers provide the flexibility to store what matters without overspending.",
					lockerColumnLabel: 'Storage Lockers (5×5)',
					regularColumnLabel: 'Regular Units (10×10)',
					rows: [
						{
							feature: 'Monthly Cost',
							locker: '$32.08 average',
							regular: '$77.06 average',
						},
						{
							feature: 'Space',
							locker: '25 sq ft',
							regular: '100 sq ft',
						},
						{
							feature: 'What Fits',
							locker: 'Seasonal decorations, clothing, sporting goods, and a few boxes',
							regular:
								"Furniture, appliances, multiple boxes, children's toys, tools",
						},
						{
							feature: 'Access',
							locker: 'Easy in/out',
							regular:
								'May need a cart or a dolly to load and unload',
						},
						{
							feature: 'Commitment',
							locker: 'Less expensive, works for short and long term needs',
							regular:
								'Larger monthly expenses, monthly minimum rental terms are most common',
						},
					],
				},
			},
		},
	],
};

const howToUseContent = [
	{
		type: 'content',
		props: {
			title: 'How to Make the Most of Your Storage Locker',
			theme: 'dark',
			contentType: 'column-type',
			content: [
				' Pack Smart\n\n- Use uniform box sizes that stack neatly\n- Label everything clearly on multiple sides\n- Place frequently needed items near the front\n- [Use vertical space](https://www.selfstorage.com/blog/vertical-closet-storage-organization-space-saving-hacks/) with sturdy shelving units',
				' Protect Your Stuff\n\n- Choose climate controlled locker storage for anything sensitive to temperature or humidity\n- Wrap furniture and electronics in protective covers\n- Use moisture absorbers in humid climates\n- Keep important documents in waterproof containers',
				" Stay Organized\n\n- [Keep an inventory list on your phone](https://www.selfstorage.com/blog/how-to-create-a-self-storage-inventory/)\n- Photograph the box contents before sealing\n- Create a simple aisle down the middle if possible\n- Review contents every few months—you might find you don't need some items anymore",
			],
		},
	},
];

export const LockerStorageContent = {
	type: 'self-storage-lockers',
	pageType: pageTypes.SGP,
	reviews: reviewsContent.reviews,
	sections: [
		{
			title: 'Locker Self Storage Hero',
			background: 'default',
			pageType: pageTypes.SGP,
			modules: [
				{
					type: 'hero',
					id: 'self-storage-lockers-hero',
					props: {
						content: heroContent,
						pageType: pageTypes.SGP,
						additionalProps: {
							heading: {
								size: 'large',
								color: 'white',
								weight: 'ultraBold',
							},
						},
					},
				},
			],
		},
		videoContent,
		{
			title: 'Locker Self Storage Pricing',
			pageType: pageTypes.SGP,
			noPadding: true,
			modules: pricingContent,
		},
		lockerComparisonContent,
		{
			title: 'How to Make the Most of Your Storage Locker',
			background: 'bgImage',
			pageType: pageTypes.SGP,
			modules: howToUseContent,
		},
		{
			title: 'Reviews Section',
			background: 'lighterBlue',
			pageType: pageTypes.SGP,
			modules: [
				{
					type: 'reviews',
					title: 'Reviews Section',
					slug: 'self-storage-lockers-reviews',
					props: {
						pageType: pageTypes.SGP,
						reviewsData: reviewsContent,
					},
				},
			],
		},

		{
			title: 'Faqs Section',
			modules: [
				{
					type: 'faqs',
					props: {
						content: faqSectionContent,
					},
				},
			],
		},
		{
			title: 'Related Content',
			background: 'darkBlue',
			modules: [
				{
					type: 'featuredContent',
					props: {
						data: relatedContent,
					},
				},
			],
		},
		linkToOtherPSPsBlock,
		{
			title: 'Storage Near You',
			modules: [
				{
					type: 'storageNearYou',
					props: {
						type: 'StorageSize',
					},
				},
			],
		},
	],
};
